import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useEditor, EditorContent } from '@tiptap/react';
// eslint-disable-next-line import/no-named-as-default
import StarterKit from '@tiptap/starter-kit';
// eslint-disable-next-line import/no-named-as-default
import Placeholder from '@tiptap/extension-placeholder';
// eslint-disable-next-line import/no-named-as-default
import CharacterCount from '@tiptap/extension-character-count';
// eslint-disable-next-line import/no-named-as-default
import Typography from '@tiptap/extension-typography';
// eslint-disable-next-line import/no-named-as-default
import Underline from '@tiptap/extension-underline';
// eslint-disable-next-line import/no-named-as-default
import TextAlign from '@tiptap/extension-text-align';
import { debugLogger } from '../../../utils/debugLogger';
// eslint-disable-next-line import/no-named-as-default
import Highlight from '@tiptap/extension-highlight';
import {
  Minus, Undo2, Redo2,
  Paintbrush,
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  Heading1, Heading2, Heading3,
  AlignLeft, AlignCenter, AlignRight,
  List, ListOrdered, TextQuote,
} from 'lucide-react';
import { Chapter } from '../../../types';
import type { EditorSelectionContext } from '../../../types';
import type { Faction, Character, Item } from '../../../types';
import { CHAPTER_STATUSES, CHAPTER_STATUS_LABELS, PLOT_THREAD_KIND_LABELS } from '@xiaotianshu/shared';
import type { PlotThreadKind } from '@xiaotianshu/shared';
import { recordWordsWritten } from '../../../utils/writingStats';
import { ensureVersionQuota } from '../../../utils/chapterVersions';
import { plainTextToHtml } from '../../../utils/editorContent';
import { saveChapterDraft, getChapterDraft, clearChapterDraft } from '../../../utils/draftStore';
import '../styles/chapterEditor.css';
import { EditorContextMenu } from './EditorContextMenu';
import { PlotThreadModal } from './PlotThreadModal';
import { FindReplacePanel } from './FindReplacePanel';
import { SearchHighlight, findQuoteRange } from '../editorSearch';
import { EditorAppearancePanel } from './EditorAppearancePanel';
import { useEditorAppearance } from '../hooks/useEditorAppearance';
import { ChapterPreviewModal } from './ChapterPreviewModal';
import { NameGeneratorModal } from '../../../components/nameGenerator/NameGeneratorModal';
import { FactionFormModal, CharacterFormModal, ItemFormModal } from '../../world-building/data-library';
import { guessItemType } from '../../../utils/nameGenerator';
import { publishEditorCommands } from '../../Editor/editorCommands';
import { notifySuccess } from '../../../utils/notify';
import { useEditorContext } from '../../Editor/context';
import { useWorkspace } from '../WorkspaceContext';

/** 自动快照间隔：距上次留档超过该时长后，自动保存时额外创建"自动保存"版本 */
const AUTO_SNAPSHOT_INTERVAL_MS = 5 * 60 * 1000;

/**
 * 内容 → TipTap HTML 的转换实现在 `utils/editorContent`（见文件顶部 import）。
 * 那里是**唯一**一份判据，与 AI 回写编辑器共用：以前这里有本地副本，而 AI 回写那两处
 * 各自复制了一份却漏掉"已是 HTML 就不包装"的判断，导致 AI 更新章节后正文被多包一层
 * `<p>`（正文开头/末尾各多出一个空段落）。单点实现后不会再出现"创建正常、更新出错"的分裂。
 */

/**
 * 格式化编辑器中的内容：
 * 将连续的纯文本段落重新整理，确保每段都用 <p> 包裹，
 * 合并空段落，去除多余空白
 */
function formatHtmlContent(html: string): string {  if (!html || !html.trim()) return '<p><br></p>';
  
  // 提取纯文本（保留换行结构）
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  
  // 获取所有文本内容，保留块级元素的换行
  const blocks = tempDiv.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, blockquote, div');
  
  if (blocks.length === 0) {
    // 没有块级元素，说明是纯文本，直接转换
    return plainTextToHtml(tempDiv.textContent || '');
  }
  
  // 已有块级结构，清理多余空行
  const result: string[] = [];
  let lastWasEmpty = false;
  
  blocks.forEach(block => {
    const text = block.textContent?.trim() || '';
    const tagName = block.tagName.toLowerCase();
    
    if (text === '') {
      // 空段落，只保留一个
      if (!lastWasEmpty) {
        result.push('<p><br></p>');
        lastWasEmpty = true;
      }
    } else {
      lastWasEmpty = false;
      if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
        result.push(`<${tagName}>${text}</${tagName}>`);
      } else if (tagName === 'li') {
        result.push(`<li>${text}</li>`);
      } else if (tagName === 'blockquote') {
        result.push(`<blockquote><p>${text}</p></blockquote>`);
      } else {
        result.push(`<p>${text}</p>`);
      }
    }
  });
  
  return result.length > 0 ? result.join('') : '<p><br></p>';
}

// ── ChapterEditor Props ──────────────────────────────────────────

interface ChapterEditorProps {
  chapter: Chapter;
  content: string;
  onSave: (title: string, summary: string, content: string) => Promise<void>;
  onEditorReady?: (editor: import('@tiptap/react').Editor) => void;
  isExternalWriting?: boolean;
  /** 右键选中文字后触发 AI 润色（润色/精简/扩写） */
  onPolishSelection?: (selection: EditorSelectionContext, mode: 'polish' | 'condense' | 'expand') => void;
  /** 右键"添加到助手"：把选中文字位置加入 AI 助手引用列表 */
  onAddToAssistant?: (selection: EditorSelectionContext) => void;
  onOpenVersionHistory?: () => void;
  onVersionSaved?: () => void;
  onUnsavedChange?: (hasUnsaved: boolean) => void;
}

// ── 主组件 ────────────────────────────────────────────────────────

export function ChapterEditor({
  chapter, content, onSave,
  onEditorReady, isExternalWriting,
  onPolishSelection, onAddToAssistant,
  onOpenVersionHistory,
  onVersionSaved,
  onUnsavedChange,
}: ChapterEditorProps) {
  const [editTitle, setEditTitle] = useState(chapter.title);
  const [editSummary, setEditSummary] = useState(chapter.summary || '');
  const [showSummary, setShowSummary] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  /**
   * 是否有自动保存正在进行。
   *
   * 必须是 ref 而不是 state：`onUpdate` 的闭包由 `useEditor` 在编辑器创建时固定，
   * 里面的 `isSaving`（state）会长期停在旧值，导致"保存往返期间的输入"被静默丢弃
   * （详见 `handleAutoSave` 的注释）。ref 读到的永远是最新值。
   */
  const saveInFlightRef = useRef(false);
  const lastSavedContentRef = useRef(content);
  const lastSavedTitleRef = useRef(chapter.title);
  const lastSavedSummaryRef = useRef(chapter.summary || '');
  const chapterRef = useRef(chapter);
  chapterRef.current = chapter;
  const editorRefForVersion = useRef<import('@tiptap/react').Editor | null>(null);
  const editTitleRef = useRef(editTitle);
  editTitleRef.current = editTitle;
  const editSummaryRef = useRef(editSummary);
  editSummaryRef.current = editSummary;
  const [liveWordCount, setLiveWordCount] = useState<number | null>(null);
  // 崩溃恢复：检测到比最近一次保存更新的草稿时展示恢复横幅
  const [draftInfo, setDraftInfo] = useState<{ savedAt: number; title: string; content: string } | null>(null);
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 手机预览：以 iPhone 外观展示当前章节的阅读效果
  const [showPreview, setShowPreview] = useState(false);
  // 取名工具：弹窗开关 + 创建入口（选择名字后 → 资料库创建弹窗预填）
  const [showNameGenerator, setShowNameGenerator] = useState(false);
  const [createEntry, setCreateEntry] = useState<{ category: 'character' | 'faction' | 'item'; name: string } | null>(null);
  const [dataLists, setDataLists] = useState<{ factions: Faction[]; characters: Character[]; items: Item[] }>({ factions: [], characters: [], items: [] });
  const { bookId, bookTitle, triggerDataRefresh, handleUpdateChapterMeta, pendingQuote, clearPendingQuote } = useWorkspace();
  // 登记伏笔后的提示里要能一键把「伏笔追踪」面板拉出来；引文定位要等切回本章节视图
  const { activeView, showPane } = useEditorContext();
  // 编辑区正文字号：仅控制编辑器正文，随 A−/A+ 调整（1px 步进，12-28px），localStorage 持久化
  const [editorFontSize, setEditorFontSize] = useState(() => {
    try {
      const v = Number(localStorage.getItem('editor-font-size'));
      return Number.isFinite(v) && v >= 12 && v <= 28 ? v : 16;
    } catch {
      return 16;
    }
  });
  useEffect(() => {
    try { localStorage.setItem('editor-font-size', String(editorFontSize)); } catch { /* ignore */ }
  }, [editorFontSize]);
  const adjustEditorFontSize = (delta: number) => {
    setEditorFontSize(prev => Math.min(28, Math.max(12, prev + delta)));
  };
  // 编辑区外观设置：背景 / 稿纸行线 / 段落间距 / 首行缩进（localStorage 持久化，仅影响显示）
  const { appearance, update: updateAppearance, reset: resetAppearance } = useEditorAppearance();
  const [showAppearancePanel, setShowAppearancePanel] = useState(false);
  const editorAppearanceClass =
    `editor-bg-${appearance.background} ` +
    `editor-rule-${appearance.ruleLines} ` +
    `editor-gap-${appearance.paragraphGap}` +
    (appearance.paragraphIndent ? ' editor-indent-on' : '');
  // 最近一次快照内容指纹（用于去重：内容相同则不重复留档）
  const lastSnapshotKeyRef = useRef('');
  const lastSnapshotAtRef = useRef(0);

  // 取名工具 → 创建弹窗联动：加载资料库列表供创建弹窗使用（父势力下拉/重名检查）
  useEffect(() => {
    if (!createEntry) return;
    let cancelled = false;
    void (async () => {
      try {
        const [factions, characters, items] = await Promise.all([
          window.api.getFactions(bookId),
          window.api.getCharacters(bookId),
          window.api.getItems(bookId),
        ]);
        if (!cancelled) setDataLists({ factions, characters, items });
      } catch { /* 列表加载失败不阻塞创建弹窗 */ }
    })();
    return () => { cancelled = true; };
  }, [createEntry, bookId]);
  // 创建保存回调（取名工具联动）：成功后刷新资料库并复制名称
  const handleCreateFromNameTool = async (
    category: 'character' | 'faction' | 'item',
    name: string,
    extra: { age?: string | null; factionId?: string | null; strength?: string | null; description?: string; type?: string; parentFactionId?: string | null },
  ) => {
    if (category === 'character') {
      await window.api.createCharacter({
        bookId, name,
        age: extra.age ?? null,
        factionId: extra.factionId ?? null,
        strength: extra.strength ?? null,
        description: extra.description ?? '',
      });
    } else if (category === 'faction') {
      await window.api.createFaction({
        bookId, name,
        parentFactionId: extra.parentFactionId ?? null,
        description: extra.description ?? '',
      });
    } else {
      await window.api.createItem({
        bookId, name,
        type: extra.type ?? (guessItemType(name) || '物品'),
        description: extra.description ?? '',
      });
    }
    triggerDataRefresh();
    try { await navigator.clipboard.writeText(name); } catch { /* 复制失败不影响创建 */ }
  };
  // 写作统计：本章节上次保存时的字数基线（用于累计每日新增字数）
  const wordBaselineRef = useRef(chapter.word_count ?? 0);
  // 是否有未保存改动（供刷新/关闭页面时提示）
  const hasUnsavedRef = useRef(hasUnsavedChanges);
  hasUnsavedRef.current = hasUnsavedChanges;

  /** 字数目标输入：防抖落库（避免每敲一位数字就发一次请求） */
  const targetWordsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleTargetWordsSave = useCallback((chapterId: string, targetWords: number): void => {
    if (targetWordsTimerRef.current) clearTimeout(targetWordsTimerRef.current);
    targetWordsTimerRef.current = setTimeout(() => {
      void handleUpdateChapterMeta(chapterId, { targetWords }).catch(() => { /* 失败保持旧值 */ });
    }, 600);
  }, [handleUpdateChapterMeta]);

  useEffect(() => () => {
    if (targetWordsTimerRef.current) clearTimeout(targetWordsTimerRef.current);
  }, []);

  const getEditorWordCount = (ed: import('@tiptap/react').Editor): number => {
    const viaStorage = ed.storage?.characterCount?.characters?.({ mode: 'textSize' });
    if (typeof viaStorage === 'number') return viaStorage;
    return ed.getText().replace(/\s/g, '').length;
  };

  const recordWritingDelta = (currentCount: number) => {
    const delta = currentCount - wordBaselineRef.current;
    if (delta > 0) recordWordsWritten(delta);
    wordBaselineRef.current = currentCount;
  };

  /** 将当前编辑器内容写入草稿快照（幂等，IndexedDB 不可用时静默降级） */
  const flushDraft = useCallback((): void => {
    const ed = editorRefForVersion.current;
    const ch = chapterRef.current;
    if (!ed || !ch) return;
    void saveChapterDraft({
      chapterId: ch.id,
      bookId: ch.book_id,
      title: editTitleRef.current,
      content: ed.getHTML(),
      savedAt: Date.now(),
    });
  }, []);

  // ── 版本快照（手动/自动/离开章节/关闭页面）──
  const createVersionSnapshot = useCallback(async (source: 'manual_save' | 'auto_save') => {
    const ed = editorRefForVersion.current;
    const ch = chapterRef.current;
    if (!ed || !ch) return;

    const content = ed.getHTML();
    const title = editTitleRef.current;
    const summary = editSummaryRef.current;
    const snapshotKey = `${content}|${title}|${summary}`;
    // 手动保存始终留档（保留原有 Ctrl+S 行为）；其余来源内容未变时跳过
    if (source !== 'manual_save' && snapshotKey === lastSnapshotKeyRef.current) return;

    const wordCount = getEditorWordCount(ed);
    try {
      await ensureVersionQuota(ch.id, source);
      await window.api.createChapterVersion({
        chapterId: ch.id,
        bookId: ch.book_id,
        title,
        summary,
        content,
        wordCount,
        source,
      });
      lastSnapshotKeyRef.current = snapshotKey;
      lastSnapshotAtRef.current = Date.now();
      onVersionSaved?.();
    } catch {
      // 版本保存失败不中断正常保存流程
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [editorContextMenu, setEditorContextMenu] = useState<{ x: number; y: number; selection: EditorSelectionContext } | null>(null);
  /** 章节内查找替换面板显隐（Ctrl+F 打开） */
  const [showFindPanel, setShowFindPanel] = useState(false);
  /** 「添加伏笔」弹窗的选中上下文（null = 关闭） */
  const [threadSelection, setThreadSelection] = useState<EditorSelectionContext | null>(null);

  /**
   * 把"工具栏右侧那批功能按钮"的命令发布给**右侧图标栏**（`PaneRail`）。
   *
   * 用户要求把这些按钮搬到右侧栏（用一个横杠与功能页隔开），但按钮在创作页层、
   * 状态在这里 —— 所以走 `editorCommands.ts` 那条极小的发布/订阅。
   */
  const canOpenVersionHistory = Boolean(onOpenVersionHistory && chapter?.id);
  useEffect(() => {
    publishEditorCommands({
      appearanceOpen: showAppearancePanel,
      toggleAppearance: () => setShowAppearancePanel(v => !v),
      findOpen: showFindPanel,
      toggleFind: () => setShowFindPanel(v => !v),
      summaryOpen: showSummary,
      toggleSummary: () => setShowSummary(v => !v),
      openNameGenerator: () => setShowNameGenerator(true),
      openPreview: () => { setShowAppearancePanel(false); setShowPreview(true); },
      historyAvailable: canOpenVersionHistory,
      openVersionHistory: () => onOpenVersionHistory?.(),
    });
    // canOpenVersionHistory 是**布尔**（不是 chapter 对象）：避免每次敲字都重新发布
  }, [showAppearancePanel, showFindPanel, showSummary, canOpenVersionHistory, onOpenVersionHistory]);

  // 编辑器主体可滚动容器 ref（用于 AI 续写时自动滚动到底部）
  const editorWrapperRef = useRef<HTMLDivElement>(null);
  // AI 续写状态 ref（供 onUpdate 闭包使用，避免 stale closure）
  const isExternalWritingRef = useRef(isExternalWriting ?? false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Placeholder.configure({
        placeholder: '开始创作你的故事...',
      }),
      CharacterCount,
      Typography,
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Highlight.configure({
        multicolor: true,
      }),
      // 查找替换的高亮（ProseMirror Decoration，不改动文档内容）
      SearchHighlight,
    ],
    content: plainTextToHtml(content || ''),
    onUpdate: () => {
      setHasUnsavedChanges(true);
      // 自动保存：3秒无操作后保存
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = setTimeout(() => {
        handleAutoSave();
      }, 3000);
      // 草稿快照：8 秒无操作后落盘（崩溃恢复用）
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
      draftTimerRef.current = setTimeout(() => flushDraft(), 8000);
      // AI 续写时自动滚动编辑器到底部，让用户透过蒙版看到文字在滚动生成
      if (isExternalWritingRef.current) {
        requestAnimationFrame(() => {
          const wrapper = editorWrapperRef.current;
          if (wrapper) wrapper.scrollTop = wrapper.scrollHeight;
        });
      }
    },
      editorProps: {
        attributes: {
          class: 'chapter-editor-content',
        },
      },
  });

  // 保持 ref 同步（供 saveChapterVersion 使用）
  editorRefForVersion.current = editor;

  // 工具栏状态渲染：selection/transaction 变化时触发重渲染（加粗激活态、撤销可用性等）
  const [, setToolbarTick] = useState(0);
  useEffect(() => {
    if (!editor) return;
    const rerender = () => setToolbarTick(t => t + 1);
    editor.on('transaction', rerender);
    return () => { editor.off('transaction', rerender); };
  }, [editor]);

  // 当章节切换时更新编辑器内容
  useEffect(() => {
    if (editor && content !== undefined) {
      const htmlContent = plainTextToHtml(content || '');
      const currentEditorContent = editor.getHTML();
      // 情况一：外部来源（切章/版本恢复/AI 回写）——内容确实不同，同步编辑器
      if (currentEditorContent !== htmlContent && htmlContent !== lastSavedContentRef.current) {
        // emitUpdate: false —— 程序化写入不应触发 onUpdate（否则会被误判为“未保存”）
        editor.commands.setContent(htmlContent, { emitUpdate: false });
        lastSavedContentRef.current = htmlContent;
        setHasUnsavedChanges(false);
        return;
      }
      // 情况二：内容一致（保存结果回显 / 外部写入与编辑器相同）——仅刷新“已保存”基线
      if (currentEditorContent === htmlContent) {
        lastSavedContentRef.current = htmlContent;
        setHasUnsavedChanges(false);
      }
      // 情况三：保存回显但编辑器已有更新的输入（用户在保存的网络往返期间继续输入/打字法组合中）。
      // 此时编辑器才是最新数据源：不能 setContent 回滚（会丢失输入并把光标重置到文末），
      // 也不能标记“已保存”，由 onUpdate 中已排定的自动保存完成最终落库。
    }
  }, [content, editor, chapter.id]);

  // 启动时检测未保存草稿（比最近保存更新的才提示）
  useEffect(() => {
    const ch = chapterRef.current;
    if (!ch) return;
    let cancelled = false;
    getChapterDraft(ch.id).then((d) => {
      if (cancelled || !d) return;
      // 草稿内容与当前已保存内容一致：属于保存/外部写入（如 AI 审批回写）后残留的
      // 冗余草稿，静默清理，避免返回章节时误报“未保存的草稿”
      const currentHtml = plainTextToHtml(content || '');
      if (d.content === currentHtml || d.content === (content || '')) {
        void clearChapterDraft(ch.id);
        return;
      }
      const savedAt = Number(d.savedAt) || 0;
      const lastSaved = Date.parse(ch.updated_at || '') || 0;
      if (savedAt > lastSaved) {
        setDraftInfo({ savedAt, title: d.title, content: d.content });
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapter.id]);

  // 页面隐藏 / 关闭前：有未保存改动则立即落盘草稿
  useEffect(() => {
    const flush = () => { if (hasUnsavedRef.current) flushDraft(); };
    // 注意：必须用命名引用注册并移除。此前 visibilitychange 传的是匿名箭头函数，
    // 而清理时移除的是 flush —— 前者永远移除不掉，每次进章节都残留一个永久监听器。
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('beforeunload', flush);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('beforeunload', flush);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [flushDraft]);

  // 卸载时清理草稿定时器
  useEffect(() => () => {
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
  }, []);

  // 当章节切换或 AI 更新章节数据时同步标题/摘要/内容引用
  useEffect(() => {
    setEditTitle(chapter.title);
    setEditSummary(chapter.summary || '');
    lastSavedTitleRef.current = chapter.title;
    lastSavedSummaryRef.current = chapter.summary || '';
    // 章节树对象不含 content 字段（undefined），仅元数据对象（getChapterMetadata、保存后回填）才携带。
    // 只在确有时同步“已保存内容”基线：否则首次进入/切章后基线被清零，
    // 保存回显（内容等于刚保存的快照）会被误判为外部写入而回滚编辑器。
    if (typeof chapter.content === 'string') {
      lastSavedContentRef.current = chapter.content;
    }
    setHasUnsavedChanges(false);
    setLastSavedAt('');
    setLiveWordCount(null);
    lastSnapshotKeyRef.current = '';
    lastSnapshotAtRef.current = 0;
    wordBaselineRef.current = chapter.word_count ?? 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapter.id, chapter.title, chapter.summary, chapter.content]);

  useEffect(() => {
    const handleClick = () => setEditorContextMenu(null);
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, []);

  const handleEditorContextMenu = (e: React.MouseEvent) => {
    if (!editor) return;
    // 弹窗打开时不弹菜单：否则右键会在弹窗之上再叠一层菜单
    if (threadSelection) return;
    const { state } = editor;
    const { selection } = state;
    if (selection.empty) return;

    e.preventDefault();
    e.stopPropagation();

    const selectedText = state.doc.textBetween(selection.from, selection.to, '\n');
    if (!selectedText.trim()) return;

    let startLine = 1, endLine = 1;
    let lineNum = 1;
    state.doc.descendants((node, offset) => {
      if (node.type.name === 'paragraph' || node.type.name === 'heading') {
        const blockEnd = offset + node.nodeSize;
        if (selection.from >= offset && selection.from < blockEnd) startLine = lineNum;
        if (selection.to > offset && selection.to <= blockEnd) endLine = lineNum;
        lineNum++;
      }
      return true;
    });

    const ctx: EditorSelectionContext = {
      text: selectedText,
      chapterTitle: chapter.title,
      chapterId: chapter.id,
      chapterNumber: chapter.chapter_number,
      from: selection.from,
      to: selection.to,
      startLine,
      endLine,
    };

    setEditorContextMenu({ x: e.clientX, y: e.clientY, selection: ctx });
  };

  const handlePolish = (sel: EditorSelectionContext, mode: 'polish' | 'condense' | 'expand') => {
    setEditorContextMenu(null);
    onPolishSelection?.(sel, mode);
  };

  // 伏笔引文定位：伏笔页点「埋于」引用后，跳回本章并选中/高亮当初标记的那段原文。
  //
  // 为什么带重试：跳转是"先切章、再等该章正文加载"，本组件拿到 pendingQuote 时
  // 正文可能还是上一章（甚至为空），此时按引文找不到东西。片段有界重试直到
  // 正文变成目标章节且能定位为止，避免为一次跳转去改切章的数据流。
  /**
   * 伏笔引文定位：伏笔页点「埋于」引用后，跳回本章并选中当初标记的那段原文。
   *
   * 两个必须等的前提，缺一个都会静默失效（都踩过）：
   *
   * 1. **视图必须可见**。父容器 `[hidden]` 时 TipTap 视图在 `display:none` 下，
   *    ProseMirror 的 `setTextSelection` 只改内部状态，原生选区落不到 DOM ——
   *    表现为"跳回来了，但没有任何选中"。因此等 `activeView === 'chapters'`。
   * 2. **正文必须是本章的**。跳转是"先切章、再等该章正文加载"，
   *    本组件拿到 pendingQuote 时正文可能还是上一章。故按引文做有界重试。
   */
  useEffect(() => {
    if (!editor || !pendingQuote || activeView !== 'chapters') return;
    if (pendingQuote.chapterId !== chapter.id) return;
    let cancelled = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const locate = () => {
      if (cancelled) return;
      attempts += 1;
      const range = findQuoteRange(editor.state.doc, pendingQuote);
      if (range) {
        // 选中 + 滚动到视野内：用户一眼看到当初埋的是哪句话。
        // 不再叠加高亮装饰 —— 原生选区（::selection）本就更醒目，也不必
        // 去动与查找替换面板共享的 SearchHighlight 状态。
        editor.chain().focus().setTextSelection(range).scrollIntoView().run();
        clearPendingQuote();
        return;
      }
      if (attempts < 30) {
        timer = setTimeout(locate, 100);
      } else {
        // 有界重试后仍找不到：这句话在原文里已不存在，不要留下"待处理"状态
        clearPendingQuote();
      }
    };

    locate();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [editor, pendingQuote, activeView, chapter.id, clearPendingQuote]);

  const handleAddToAssistant = (sel: EditorSelectionContext) => {
    setEditorContextMenu(null);
    onAddToAssistant?.(sel);
  };

  /** 右键「添加为伏笔」：关掉菜单，打开登记弹窗（类型 + 备注） */
  const handleAddToPlotThread = (sel: EditorSelectionContext) => {
    setEditorContextMenu(null);
    setThreadSelection(sel);
  };

  /** 伏笔登记成功：刷新资料（伏笔页下次进入即是最新），并提示可一键去看 */
  const handlePlotThreadCreated = (payload: { title: string; kind: PlotThreadKind }) => {
    setThreadSelection(null);
    triggerDataRefresh();
    notifySuccess(
      `已登记${PLOT_THREAD_KIND_LABELS[payload.kind]}：${payload.title}`,
      // 伏笔追踪已迁到右侧面板：这里把面板**拉出来**（showPane 对已在分屏里的页不做任何事），
      // 而不是切中心区域的页 —— 正文与章节编辑状态都不会因此丢失
      { label: '打开伏笔追踪', onClick: () => showPane('threads') },
    );
  };

  /**
   * 是否该为本次保存留一个"自动保存"版本（距上次留档超过 5 分钟）。
   *
   * 单独抽成 useCallback 而不是内联在 `handleAutoSave` 里：内联写法会让
   * `react-hooks/purity` 把 `Date.now()` 当成渲染期读取时钟而报 error
   * （`handleAutoSave` 实际只在 setTimeout 里调用，是误判）。抽出来后
   * 「读时钟」被隔离在一个明确的回调里，规则不再误触发，语义也更直白。
   */
  const shouldCreateAutoSnapshot = useCallback((): boolean => {
    return Date.now() - lastSnapshotAtRef.current >= AUTO_SNAPSHOT_INTERVAL_MS;
  }, []);

  const handleAutoSave = async () => {
    /**
     * 用 ref 而不是 state 判断"是否有保存正在进行"。
     *
     * 为什么不能用 `isSaving`：`onUpdate`（`useEditor` 配置）是编辑器实例创建时
     * 固定的闭包，它排定的定时器捕获的是**当时那一份** `handleAutoSave`；
     * 而 `isSaving` 是 state，那份闭包里的值会长期停在旧值。结果是"在保存往返期间
     * 输入 → 防抖到期时读到过期的 isSaving=true → 静默 return 且不再重排"，
     * 这批输入永远不落库，而界面上的「未保存」标记还亮着 —— 用户完全看不出来。
     * ref 始终读最新值，不受闭包捕获影响。
     */
    if (!editor || saveInFlightRef.current) return;
    const htmlContent = editor.getHTML();
    const title = editTitleRef.current;
    const summary = editSummaryRef.current;

    if (htmlContent === lastSavedContentRef.current &&
        title === lastSavedTitleRef.current &&
        summary === lastSavedSummaryRef.current) {
      setHasUnsavedChanges(false);
      debugLogger.system('AUTO_SAVE_SKIPPED', { reason: 'no_changes', title, summaryLen: summary.length, contentLen: htmlContent.length });
      return;
    }

    // 立即记录"正在保存的内容"基线（不等网络往返）：
    // 保存回显的 setChapterContent 可能在 await 间隙就触发渲染，
    // 若基线未更新，内容同步 effect 会把保存快照（旧内容）setContent 回编辑器，
    // 回滚用户在保存期间的新输入并把光标重置到文末。
    const baselineBeforeSaveRef = lastSavedContentRef.current;
    lastSavedContentRef.current = htmlContent;

    debugLogger.system('AUTO_SAVE_FIRED', { title, summaryLen: summary.length, contentLen: htmlContent.length, lastTitle: lastSavedTitleRef.current, lastSummary: lastSavedSummaryRef.current });
    saveInFlightRef.current = true;
    setIsSaving(true);
    try {
      await onSave(title, summary, htmlContent);
      await clearChapterDraft(chapterRef.current.id);
      // 保存成功：取消待触发的草稿快照定时器，
      // 避免保存后定时器再次写入草稿（下次进入章节会误报“未保存的草稿”）
      if (draftTimerRef.current) {
        clearTimeout(draftTimerRef.current);
        draftTimerRef.current = null;
      }
      setDraftInfo(null);
      lastSavedContentRef.current = htmlContent;
      lastSavedTitleRef.current = title;
      lastSavedSummaryRef.current = summary;
      setLastSavedAt(new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }));
      setHasUnsavedChanges(false);
      recordWritingDelta(getEditorWordCount(editor));
      // 自动快照：距上次留档超过 5 分钟时创建"自动保存"版本，防止误操作丢失
      if (shouldCreateAutoSnapshot()) {
        void createVersionSnapshot('auto_save');
      }
      // 补一次保存：本次往返期间用户又输入了内容。
      // 该输入的防抖定时器到期时正好撞上"保存中"而被跳过，若不在这里补排，
      // 这批内容要等用户**再打一次字**才会保存 —— 期间界面还显示着「未保存」。
      // 用较短的延迟（而非完整 3s 防抖）：这些字已经等了整个往返，
      // 若用户仍在连续输入，下一次 onUpdate 会重排完整防抖，不会造成每 0.5s 一次的频繁落库。
      saveInFlightRef.current = false;
      if (editor.getHTML() !== lastSavedContentRef.current && !autoSaveTimerRef.current) {
        autoSaveTimerRef.current = setTimeout(() => { void handleAutoSave(); }, 500);
      }
    } catch {
      // 保存失败：还原"已保存"基线（内容实际未落库），下次编辑时自动保存会重试
      lastSavedContentRef.current = baselineBeforeSaveRef;
      saveInFlightRef.current = false;
      // save failure is non-critical, skip
    } finally {
      setIsSaving(false);
    }
  };

  const handleManualSave = async () => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    if (!editor || isSaving) return;
    const htmlContent = editor.getHTML();
    const title = editTitleRef.current;
    const summary = editSummaryRef.current;

    // 与自动保存一致：先记录基线，防止保存回显的渲染间隙回滚编辑器
    const baselineBeforeSaveRef = lastSavedContentRef.current;
    lastSavedContentRef.current = htmlContent;

    setIsSaving(true);
    try {
      await onSave(title, summary, htmlContent);
      await clearChapterDraft(chapterRef.current.id);
      // 保存成功：取消待触发的草稿快照定时器，防止保存后再次写入草稿
      if (draftTimerRef.current) {
        clearTimeout(draftTimerRef.current);
        draftTimerRef.current = null;
      }
      setDraftInfo(null);
      lastSavedContentRef.current = htmlContent;
      lastSavedTitleRef.current = title;
      lastSavedSummaryRef.current = summary;
      setLastSavedAt(new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }));
      setHasUnsavedChanges(false);
      recordWritingDelta(getEditorWordCount(editor));
      await createVersionSnapshot('manual_save');
    } catch {
      // 保存失败：还原"已保存"基线（内容实际未落库），下次编辑时自动保存会重试
      lastSavedContentRef.current = baselineBeforeSaveRef;
      // save failure is non-critical, skip
    } finally {
      setIsSaving(false);
    }
  };

  const handleRestoreDraft = () => {
    if (!draftInfo || !editor) return;
    editor.commands.setContent(draftInfo.content, { emitUpdate: false });
    setHasUnsavedChanges(true);
    setDraftInfo(null);
  };

  const handleDiscardDraft = () => {
    if (!draftInfo) return;
    void clearChapterDraft(chapterRef.current.id);
    setDraftInfo(null);
  };

  const draftAgeText = draftInfo
    ? new Date(draftInfo.savedAt).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    : '';

  // 组件卸载：清理自动保存定时器
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, []);

  // 刷新/关闭页面：如有未保存改动，提示用户是否保存（浏览器原生确认框）
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // 键盘快捷键：Ctrl+S / Ctrl+Enter 手动保存并留档
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key === 's') {
        e.preventDefault();
        handleManualSave();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleManualSave();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, editTitle, editSummary, isSaving]);

  // 章节内查找替换：Ctrl+F 打开/聚焦面板（Esc 由面板自行关闭）。
  // 仅在创作页可见时生效，避免在资料库等视图里抢走快捷键。
  useEffect(() => {
    const onFindKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.key !== 'f') return;
      // 编辑器所在视图被隐藏时（activeView !== 'chapters'）不接管
      if (editorWrapperRef.current?.closest('[hidden]')) return;
      e.preventDefault();
      setShowFindPanel(true);
    };
    window.addEventListener('keydown', onFindKey);
    return () => window.removeEventListener('keydown', onFindKey);
  }, []);

  // 实时字数：随编辑即时更新（状态栏展示）
  useEffect(() => {
    if (!editor) return;
    const update = () => setLiveWordCount(getEditorWordCount(editor));
    editor.on('update', update);
    return () => { editor.off('update', update); };
  }, [editor]);

  // 剧情概览页补写/编辑摘要后同步本编辑器（隐藏状态下仍挂载，避免旧摘要后续被自动保存覆盖）
  useEffect(() => {
    const onSummaryUpdated = (e: Event) => {
      const detail = (e as CustomEvent<{ chapterId: string; summary: string }>).detail;
      if (!detail || detail.chapterId !== chapterRef.current?.id) return;
      // 编辑器有未保存改动时跳过（用户正在编辑，保存时以其输入为准）
      if (hasUnsavedRef.current) return;
      editSummaryRef.current = detail.summary;
      lastSavedSummaryRef.current = detail.summary;
      setEditSummary(detail.summary);
    };
    window.addEventListener('xiaotianshu:summary-updated', onSummaryUpdated);
    return () => window.removeEventListener('xiaotianshu:summary-updated', onSummaryUpdated);
  }, []);

  // 通知父组件 editor 实例就绪（T4A.3）
  useEffect(() => {
    if (editor && onEditorReady) {
      onEditorReady(editor);
    }
  }, [editor, onEditorReady]);

  // 把 editor 实例挂到 window 上供 E2E 断言使用：
  // 「伏笔页跳回正文并选中原文」是选区状态，而原生 window.getSelection() 在
  // 无头/无焦点时不一定反映编辑器状态；直接读 ProseMirror 的选区才是要锁的行为。
  useEffect(() => {
    window.__xtsEditor = editor ?? null;
    return () => { window.__xtsEditor = null; };
  }, [editor]);

  // 同步未保存状态到父组件
  useEffect(() => {
    onUnsavedChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedChange]);

  // 保持 isExternalWritingRef 与 prop 同步（供 onUpdate 闭包使用）
  useEffect(() => {
    isExternalWritingRef.current = isExternalWriting ?? false;
  }, [isExternalWriting]);

  // AI 续写时锁定编辑器可编辑性
  useEffect(() => {
    if (!editor) return;
    // 第二参数 false —— 切换可编辑状态不触发 onUpdate（否则会被误判为“未保存”）
    editor.setEditable(!isExternalWriting, false);
  }, [editor, isExternalWriting]);

  if (!editor) return null;

  // 查找工具栏 portal 目标
  const toolbarSlot = document.getElementById('cm-toolbar-slot');
  const statusbarSlot = document.getElementById('cm-statusbar-slot');

  return (
    <div
      className={`chapter-editor-container ${editorAppearanceClass}`}
      style={{ ['--editor-font-size']: `${editorFontSize}px` } as React.CSSProperties}
    >
      {/* 工具栏 - 通过 Portal 传送到顶部全宽区域 */}
      {toolbarSlot && createPortal(
        <div className="editor-toolbar">
        <div className="toolbar-group">
          <button
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            title="撤销 (Ctrl+Z)"
          >
            <Undo2 size={16} />
          </button>
          <button
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            title="重做 (Ctrl+Y)"
          >
            <Redo2 size={16} />
          </button>
        </div>

        <div className="toolbar-divider" />

        {/* 行内样式：加粗 / 斜体 / 下划线 / 删除线 */}
        <div className="toolbar-group">
          <button
            className={editor.isActive('bold') ? 'is-active' : ''}
            onClick={() => editor.chain().focus().toggleBold().run()}
            title="加粗 (Ctrl+B)"
          >
            <Bold size={16} />
          </button>
          <button
            className={editor.isActive('italic') ? 'is-active' : ''}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            title="斜体 (Ctrl+I)"
          >
            <Italic size={16} />
          </button>
          <button
            className={editor.isActive('underline') ? 'is-active' : ''}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            title="下划线 (Ctrl+U)"
          >
            <UnderlineIcon size={16} />
          </button>
          <button
            className={editor.isActive('strike') ? 'is-active' : ''}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            title="删除线"
          >
            <Strikethrough size={16} />
          </button>
        </div>

        {/* 标题 */}
        <div className="toolbar-group toolbar-heading-group">
          {([1, 2, 3] as const).map(level => (
            <button
              key={level}
              className={`toolbar-text-btn${editor.isActive('heading', { level }) ? ' is-active' : ''}`}
              onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
              title={`${level}级标题`}
            >
              H{level}
            </button>
          ))}
        </div>

        <div className="toolbar-divider" />

        {/* 对齐 */}
        <div className="toolbar-group">
          <button
            className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            title="左对齐"
          >
            <AlignLeft size={16} />
          </button>
          <button
            className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            title="居中"
          >
            <AlignCenter size={16} />
          </button>
          <button
            className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            title="右对齐"
          >
            <AlignRight size={16} />
          </button>
        </div>

        <div className="toolbar-divider" />

        {/* 列表 / 引用 */}
        <div className="toolbar-group">
          <button
            className={editor.isActive('bulletList') ? 'is-active' : ''}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            title="无序列表"
          >
            <List size={16} />
          </button>
          <button
            className={editor.isActive('orderedList') ? 'is-active' : ''}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            title="有序列表"
          >
            <ListOrdered size={16} />
          </button>
          <button
            className={editor.isActive('blockquote') ? 'is-active' : ''}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            title="引用"
          >
            <TextQuote size={16} />
          </button>
        </div>

        <div className="toolbar-divider" />

        <div className="toolbar-group">
          <button
            onClick={() => editor.chain().focus().insertContent('<p>————</p>').run()}
            title="场景分隔符：在光标处插入一行破折号，用于分隔场景/剧情"
          >
            <Minus size={16} />
          </button>
          <button
            onClick={() => {
              const currentHtml = editor.getHTML();
              const formatted = formatHtmlContent(currentHtml);
              editor.commands.setContent(formatted, { emitUpdate: false });
              setHasUnsavedChanges(true);
            }}
            title="格式化：重新分段并清理多余空行"
          >
            <Paintbrush size={16} />
          </button>
        </div>

        <div className="toolbar-divider" />

        {/* 编辑区文字大小：仅作用于编辑器正文，不随 UI 缩放 */}
        <div className="toolbar-group">
          <button
            className="toolbar-text-btn"
            onClick={() => adjustEditorFontSize(-1)}
            title={`减小编辑区文字（当前 ${editorFontSize}px）`}
          >
            A−
          </button>
          <button
            className="toolbar-text-btn"
            onClick={() => adjustEditorFontSize(1)}
            title={`增大编辑区文字（当前 ${editorFontSize}px）`}
          >
            A+
          </button>
        </div>

        {/* ⚠️ 这里原来还有一组"右侧功能按钮"（编辑区外观 / 取名工具 / 查找替换 / 预览 /
            历史版本 / 章节摘要）与保存状态。按用户要求已搬走：
              · 那 6 个功能按钮 → **右侧图标栏**（`PaneRail`，与上方功能页用一个横杠隔开），
                接线见 `pages/Editor/editorCommands.ts`（编辑器发布命令、图标栏订阅渲染）；
              · 保存状态 → **底部信息栏右侧**（`.editor-status-bar`）。
            工具栏因此瘦了一大截，窄窗口下也更不容易折行（它只有 flex-wrap，没有最小宽度）。 */}
      </div>,
        toolbarSlot
      )}

      {/* 摘要区域（可折叠） */}
      {showSummary && (
        <div className="chapter-summary-area">
          <label className="summary-label">章节摘要</label>
          <textarea
            value={editSummary}
            onChange={(e) => {
              setEditSummary(e.target.value);
              setHasUnsavedChanges(true);
              if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
              autoSaveTimerRef.current = setTimeout(() => handleAutoSave(), 3000);
            }}
            placeholder="为这个章节写一段简短的摘要..."
            className="summary-textarea"
            rows={3}
          />
        </div>
      )}

      {/* 手机预览弹窗：居中于编辑区域，非整页 */}
      {showPreview && (
        <ChapterPreviewModal
          bookTitle={bookTitle}
          chapterNumber={chapter.chapter_number}
          title={editTitle}
          content={editor ? editor.getHTML() : ''}
          onClose={() => setShowPreview(false)}
        />
      )}
      {draftInfo && (
        <div className="draft-recovery-banner">
          <span className="draft-recovery-text">
            💾 检测到未保存的草稿（{draftAgeText} 自动备份）
          </span>
          <div className="draft-recovery-actions">
            <button className="draft-recovery-restore" onClick={handleRestoreDraft}>恢复</button>
            <button className="draft-recovery-discard" onClick={handleDiscardDraft}>丢弃</button>
          </div>
        </div>
      )}
      <div className="chapter-title-area">
        <input
          value={editTitle}
          onChange={(e) => {
            setEditTitle(e.target.value);
            setHasUnsavedChanges(true);
            if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = setTimeout(() => handleAutoSave(), 3000);
          }}
          placeholder="章节标题"
          className="chapter-title-input"
        />
      </div>

      {/* ── 编辑器主体容器 ── */}
      <div className="editor-area-wrapper">
        {/* 章节内查找替换（Ctrl+F）：此前 Ctrl+F 被劫持为只读全局搜索，没有任何替换能力 */}
        {showFindPanel && editor && (
          <FindReplacePanel editor={editor} onClose={() => setShowFindPanel(false)} />
        )}
        <div className="editor-wrapper" ref={editorWrapperRef} onContextMenu={handleEditorContextMenu}>
          <EditorContent editor={editor} />
        </div>
      </div>

      {/* 编辑区外观设置弹窗（背景/行线/段落间距/首行缩进） */}
      {showAppearancePanel && (
        <EditorAppearancePanel
          appearance={appearance}
          onUpdate={updateAppearance}
          onReset={resetAppearance}
          onClose={() => setShowAppearancePanel(false)}
        />
      )}

      {/* 底部状态栏 - 通过 Portal 传送到全宽底部区域 */}
      {statusbarSlot && createPortal(
        <div className="editor-status-bar">
          <div className="status-left">
            <span className="word-count">{liveWordCount ?? chapter.word_count ?? 0} 字</span>
            {chapter.target_words > 0 && (
              <span
                className={`word-target${(liveWordCount ?? chapter.word_count ?? 0) >= chapter.target_words ? ' word-target-met' : ''}`}
                title="本章字数目标（在下方可修改）"
              >
                / {chapter.target_words} 字
              </span>
            )}
            <span className="status-divider">|</span>
            {/* 章节写作状态：长篇需要一眼看出哪些章还没改完 */}
            <div className="chapter-status-picker" role="group" aria-label="章节写作状态">
              {CHAPTER_STATUSES.map(s => (
                <button
                  key={s}
                  className={`chapter-status-option${(chapter.status ?? 'draft') === s ? ' is-active' : ''}`}
                  title={CHAPTER_STATUS_LABELS[s]}
                  aria-pressed={(chapter.status ?? 'draft') === s}
                  onClick={() => { void handleUpdateChapterMeta(chapter.id, { status: s }); }}
                >
                  <span className={`chapter-status-dot chapter-status-${s}`} />
                  {CHAPTER_STATUS_LABELS[s]}
                </button>
              ))}
            </div>
            <span className="status-divider">|</span>
            {/* 每章字数目标：0 表示不设目标；输入即防抖落库，无需额外本地态 */}
            <label className="word-target-input" title="本章字数目标（0 = 不设目标）">
              目标
              <input
                type="number"
                min="0"
                step="100"
                value={chapter.target_words ?? 0}
                onChange={(e) => {
                  const next = Math.max(0, Math.floor(Number(e.target.value) || 0));
                  scheduleTargetWordsSave(chapter.id, next);
                }}
              />
              字
            </label>
            <span className="status-divider">|</span>
            <span className="chapter-meta">
              创建于 {chapter.created_at ? new Date(chapter.created_at).toLocaleDateString('zh-CN') : '未知'}
            </span>
            <span className="status-divider">|</span>
            <span className="chapter-meta">
              修改于 {chapter.updated_at ? new Date(chapter.updated_at).toLocaleString('zh-CN', { hour: '2-digit', minute: '2-digit' }) : '未知'}
            </span>
          </div>
          {/* 右侧：保存状态（原来在工具栏右侧，按用户要求搬到信息栏右侧）。
              用户明确要求"当成 label"—— 所以视觉上就是**一颗指示灯 + 文字**：
              无边框、无背景、无固定宽度（此前那个圆角边框来自全局 button 样式，
              加上为容纳"已保存 23:59"设的 88px 固定宽度，看着就成了个丑按钮）。
              语义上仍是 button：可点 = 立即保存、可 Tab 聚焦；最近保存时间放进 title。 */}
          <div className="status-right">
            <button
              onClick={handleManualSave}
              className={`save-status${isSaving ? ' is-saving' : (hasUnsavedChanges ? ' has-changes' : ' is-saved')}`}
              title={lastSavedAt ? `保存 (Ctrl+S)，最近保存 ${lastSavedAt}` : '保存 (Ctrl+S)'}
            >
              {isSaving ? '保存中…' : (hasUnsavedChanges ? '未保存' : '已保存')}
            </button>
          </div>
        </div>,
        statusbarSlot
      )}

      {editorContextMenu && (
        <EditorContextMenu
          x={editorContextMenu.x}
          y={editorContextMenu.y}
          selection={editorContextMenu.selection}
          onPolish={handlePolish}
          onAddToAssistant={handleAddToAssistant}
          onAddToPlotThread={handleAddToPlotThread}
        />
      )}

      {/* 右键「添加为伏笔」弹窗：选类型 + 写备注，埋点固定为当前章节 */}
      {threadSelection && (
        <PlotThreadModal
          bookId={bookId}
          selection={threadSelection}
          onClose={() => setThreadSelection(null)}
          onCreated={handlePlotThreadCreated}
        />
      )}

      {/* 取名工具弹窗：创建弹窗打开时置灰保持状态（取消后返回继续选名） */}
      {showNameGenerator && (
        <NameGeneratorModal
          bookId={bookId}
          paused={!!createEntry}
          onClose={() => setShowNameGenerator(false)}
          onCreateEntry={(category, name) => {
            // 不关闭取名窗：关闭确认窗后在其上叠加创建弹窗，取消创建可回到此处继续选名
            setCreateEntry({ category, name });
          }}
        />
      )}

      {/* 取名 → 创建资料库条目（预填名字，保存后刷新资料库并全关） */}
      {createEntry?.category === 'character' && (
        <CharacterFormModal
          factions={dataLists.factions}
          characters={dataLists.characters}
          presetName={createEntry.name}
          onClose={() => setCreateEntry(null)}
          onSave={async (name, age, factionId, strength, description) => {
            await handleCreateFromNameTool('character', name, { age, factionId, strength, description });
            setCreateEntry(null);
            setShowNameGenerator(false);
          }}
        />
      )}
      {createEntry?.category === 'faction' && (
        <FactionFormModal
          factions={dataLists.factions}
          presetName={createEntry.name}
          onClose={() => setCreateEntry(null)}
          onSave={async (name, parentFactionId, description) => {
            await handleCreateFromNameTool('faction', name, { parentFactionId, description });
            setCreateEntry(null);
            setShowNameGenerator(false);
          }}
        />
      )}
      {createEntry?.category === 'item' && (
        <ItemFormModal
          items={dataLists.items}
          presetName={createEntry.name}
          presetType={guessItemType(createEntry.name) || undefined}
          onClose={() => setCreateEntry(null)}
          onSave={async (name, type, description) => {
            await handleCreateFromNameTool('item', name, { type, description });
            setCreateEntry(null);
            setShowNameGenerator(false);
          }}
        />
      )}
    </div>
  );
}
